import type { ReactNode } from 'react'
import type { AgentAvatar } from '@algoboo/schema/api'
import { SoftAgent } from '../avatar/SoftAgent.js'
import { cx } from './cx.js'
import { Button } from './primitives.js'

const SLEEPY: AgentAvatar = { family: 'nimbus', palette: 'lilac', expression: 'sleepy', material: 'frosted' }
const WORRIED: AgentAvatar = { family: 'bop', palette: 'peach', expression: 'curious', material: 'matte' }

export function EmptyState({
  title,
  body,
  action,
  mascot = SLEEPY,
  compact,
  className,
}: {
  title: ReactNode
  body?: ReactNode
  action?: ReactNode
  mascot?: AgentAvatar
  compact?: boolean
  className?: string
}) {
  return (
    <div className={cx('rh-state flex flex-col items-center px-4 text-center', compact ? 'gap-2 py-6' : 'gap-3 py-10', className)}>
      <SoftAgent avatar={mascot} size={compact ? 48 : 72} framing="full" title="" />
      <div className="max-w-sm">
        <p className="font-display text-[15px] font-semibold text-ink">{title}</p>
        {body && <p className="mt-1 text-[13px] text-ink-3">{body}</p>}
      </div>
      {action}
    </div>
  )
}

export function ErrorState({
  title = 'Something went sideways',
  body,
  code,
  onRetry,
  action,
  compact,
  className,
}: {
  title?: ReactNode
  body?: ReactNode
  code?: string | undefined
  onRetry?: (() => void) | undefined
  action?: ReactNode
  compact?: boolean
  className?: string
}) {
  return (
    <div role="alert" className={cx('rh-state flex flex-col items-center px-4 text-center', compact ? 'gap-2 py-6' : 'gap-3 py-10', className)}>
      <SoftAgent avatar={WORRIED} size={compact ? 48 : 72} framing="full" title="" />
      <div className="max-w-md">
        <p className="font-display text-[15px] font-semibold text-ink">{title}</p>
        {body && <p className="mt-1 text-[13px] text-ink-2">{body}</p>}
        {code && <p className="mt-2 font-mono text-[11px] text-ink-3">code {code}</p>}
      </div>
      <div className="flex gap-2">
        {onRetry && (
          <Button size="sm" icon="refresh" onClick={onRetry}>
            Try again
          </Button>
        )}
        {action}
      </div>
    </div>
  )
}
